import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import { useAuthStore } from './auth-store';

// Maximum tool call rounds to prevent infinite loops
const MAX_TOOL_ROUNDS = 5;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  toolCalls?: Array<{ id: string; name: string; parameters: any }>;
  toolResults?: Array<{ toolCallId: string; result: any }>;
  timestamp: string;
}

export interface Conversation {
  id: string;
  propertyId: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

interface AIToolCall {
  id: string;
  name: string;
  parameters: Record<string, any>;
}

interface ChatState {
  // State
  conversations: Record<string, Conversation>;
  activeConversationId: string | null;
  isStreaming: boolean;
  streamingContent: string;
  selectedModel: string | null;

  // Getters
  getActiveConversation: () => Conversation | null;
  getConversationsForProperty: (propertyId: string) => Conversation[];

  // Actions
  startConversation: (propertyId: string) => string;
  sendMessage: (content: string) => Promise<void>;
  addMessage: (message: ChatMessage) => void;
  updateStreamingContent: (content: string) => void;
  setStreaming: (isStreaming: boolean) => void;
  clearConversation: (conversationId?: string) => void;
  setActiveConversation: (conversationId: string | null) => void;
  setSelectedModel: (model: string) => void;
}

/**
 * Generate a unique ID
 */
function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Execute a tool call using the HTTP API
 */
async function executeToolCall(
  toolCall: AIToolCall,
  propertyId: string,
  siteUrl?: string
): Promise<unknown> {
  const { name, parameters } = toolCall;

  try {
    switch (name) {
      case 'runGAReport': {
        const { dateRange, dimensions, metrics, dimensionFilter, orderBy, limit } = parameters;

        // Get GA4 property ID
        const properties = useAuthStore.getState().properties;
        const property = properties.find((p) => p.id === propertyId);
        const ga4PropertyId = property?.ga4Property?.id?.replace('properties/', '') || propertyId;

        const result = await api.google.runGAReport({
          propertyId: ga4PropertyId,
          dimensions,
          metrics,
          dateRange,
          dimensionFilter,
          orderBy,
          limit: limit || 10,
        });

        return result;
      }

      case 'runGSCQuery': {
        const { dimensions, startDate, endDate, filters, rowLimit } = parameters;

        // Get GSC site URL
        const properties = useAuthStore.getState().properties;
        const property = properties.find((p) => p.id === propertyId);
        const gscSiteUrl = property?.gscSite?.siteUrl || siteUrl;

        if (!gscSiteUrl) {
          return { error: 'No Search Console site connected for this property' };
        }

        const result = await api.google.runGSCQuery({
          siteUrl: gscSiteUrl,
          dimensions,
          startDate,
          endDate,
          filters,
          rowLimit: rowLimit || 10,
        });

        return result;
      }

      case 'comparePeriods': {
        const { metrics, period1, period2, dimensions } = parameters;

        // Get GA4 property ID
        const properties = useAuthStore.getState().properties;
        const property = properties.find((p) => p.id === propertyId);
        const ga4PropertyId = property?.ga4Property?.id?.replace('properties/', '') || propertyId;

        // Run both periods in parallel
        const [result1, result2] = await Promise.all([
          api.google.runGAReport({
            propertyId: ga4PropertyId,
            dimensions,
            metrics,
            dateRange: period1,
          }),
          api.google.runGAReport({
            propertyId: ga4PropertyId,
            dimensions,
            metrics,
            dateRange: period2,
          }),
        ]);

        return { period1: result1, period2: result2 };
      }

      case 'getTopContent': {
        const { metric, dateRange, limit, pathFilter } = parameters;

        // Get GA4 property ID
        const properties = useAuthStore.getState().properties;
        const property = properties.find((p) => p.id === propertyId);
        const ga4PropertyId = property?.ga4Property?.id?.replace('properties/', '') || propertyId;

        // Map metric names
        const metricMap: Record<string, string> = {
          pageviews: 'screenPageViews',
          sessions: 'sessions',
          users: 'totalUsers',
          engagedSessions: 'engagedSessions',
          averageSessionDuration: 'averageSessionDuration',
        };

        const result = await api.google.runGAReport({
          propertyId: ga4PropertyId,
          dimensions: ['pagePath'],
          metrics: [metricMap[metric] || metric],
          dateRange,
          dimensionFilter: pathFilter
            ? {
                filter: {
                  fieldName: 'pagePath',
                  stringFilter: { matchType: 'CONTAINS', value: pathFilter },
                },
              }
            : undefined,
          orderBy: { metric: metricMap[metric] || metric, desc: true },
          limit: limit || 10,
        });

        return result;
      }

      default:
        return { error: `Unknown tool: ${name}` };
    }
  } catch (error) {
    console.error(`Tool execution error (${name}):`, error);
    return { error: error instanceof Error ? error.message : 'Tool execution failed' };
  }
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      conversations: {},
      activeConversationId: null,
      isStreaming: false,
      streamingContent: '',
      selectedModel: null,

      // Get active conversation
      getActiveConversation: () => {
        const { conversations, activeConversationId } = get();
        if (!activeConversationId) return null;
        return conversations[activeConversationId] || null;
      },

      // Get conversations for a property
      getConversationsForProperty: (propertyId) => {
        const { conversations } = get();
        return Object.values(conversations)
          .filter((c) => c.propertyId === propertyId)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      },

      // Start a new conversation
      startConversation: (propertyId) => {
        const id = generateId();
        const now = new Date().toISOString();

        const conversation: Conversation = {
          id,
          propertyId,
          messages: [],
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          conversations: {
            ...state.conversations,
            [id]: conversation,
          },
          activeConversationId: id,
        }));

        return id;
      },

      // Set selected model
      setSelectedModel: (model) => {
        set({ selectedModel: model });
      },

      // Send a message
      sendMessage: async (content) => {
        const { activeConversationId, conversations, selectedModel } = get();
        if (!activeConversationId) return;

        const conversation = conversations[activeConversationId];
        if (!conversation) return;

        // Add user message
        const userMessage: ChatMessage = {
          id: generateId(),
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };

        get().addMessage(userMessage);

        // Start streaming
        set({ isStreaming: true, streamingContent: 'Thinking...' });

        try {
          // Get the active property's GSC site URL if available
          const properties = useAuthStore.getState().properties;
          const property = properties.find((p) => p.id === conversation.propertyId);
          const siteUrl = property?.gscSite?.siteUrl;

          // Call AI API via HTTP
          let response = await api.ai.query({
            query: content,
            context: {
              propertyId: conversation.propertyId,
              siteUrl,
            },
            conversationId: activeConversationId,
            model: selectedModel || undefined,
          });

          // Handle tool calls loop
          let toolRounds = 0;
          while (response?.toolCalls && response.toolCalls.length > 0 && toolRounds < MAX_TOOL_ROUNDS) {
            toolRounds++;
            set({ streamingContent: `Analyzing data (step ${toolRounds})...` });

            console.log(`Executing ${response.toolCalls.length} tool calls (round ${toolRounds})`);

            // Execute all tool calls in parallel
            const toolResults = await Promise.all(
              response.toolCalls.map(async (toolCall) => {
                const result = await executeToolCall(toolCall, conversation.propertyId, siteUrl);
                return {
                  toolCallId: toolCall.id,
                  result,
                };
              })
            );

            // Continue conversation with tool results
            response = await api.ai.continue({
              conversationId: response.conversationId,
              toolResults,
            });
          }

          // Add assistant response
          if (response?.response) {
            const assistantMessage: ChatMessage = {
              id: generateId(),
              role: 'assistant',
              content: response.response,
              toolCalls: response.toolCalls,
              timestamp: new Date().toISOString(),
            };

            get().addMessage(assistantMessage);
          } else if (toolRounds >= MAX_TOOL_ROUNDS) {
            // Max rounds reached
            const errorMessage: ChatMessage = {
              id: generateId(),
              role: 'assistant',
              content: 'I had to stop after multiple data lookups. Could you try a more specific question?',
              timestamp: new Date().toISOString(),
            };

            get().addMessage(errorMessage);
          }
        } catch (error) {
          console.error('Failed to send message:', error);

          // Add error message
          const errorMessage: ChatMessage = {
            id: generateId(),
            role: 'assistant',
            content: 'Sorry, I encountered an error processing your request. Please try again.',
            timestamp: new Date().toISOString(),
          };

          get().addMessage(errorMessage);
        } finally {
          set({ isStreaming: false, streamingContent: '' });
        }
      },

      // Add a message to the active conversation
      addMessage: (message) => {
        const { activeConversationId } = get();
        if (!activeConversationId) return;

        set((state) => {
          const conversation = state.conversations[activeConversationId];
          if (!conversation) return state;

          return {
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...conversation,
                messages: [...conversation.messages, message],
                updatedAt: new Date().toISOString(),
              },
            },
          };
        });
      },

      // Update streaming content
      updateStreamingContent: (content) => {
        set({ streamingContent: content });
      },

      // Set streaming state
      setStreaming: (isStreaming) => {
        set({ isStreaming });
        if (!isStreaming) {
          set({ streamingContent: '' });
        }
      },

      // Clear conversation
      clearConversation: (conversationId) => {
        const id = conversationId || get().activeConversationId;
        if (!id) return;

        set((state) => {
          const { [id]: _, ...rest } = state.conversations;
          return {
            conversations: rest,
            activeConversationId: state.activeConversationId === id ? null : state.activeConversationId,
          };
        });
      },

      // Set active conversation
      setActiveConversation: (conversationId) => {
        set({ activeConversationId: conversationId });
      },
    }),
    {
      name: 'chartzen-web-chat',
      partialize: (state) => ({
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
        selectedModel: state.selectedModel,
      }),
    }
  )
);
